import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Search, MapPin, RefreshCw, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const emptyForm = { city: "", colonia: "", factor: "1.00", notes: "" };

const authHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ColoniasManager() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/valuation/colonias`, { headers: authHeader() });
      setList(res.data?.colonias || []);
    } catch (e) {
      toast.error("Error al cargar colonias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cities = useMemo(() => {
    const set = new Set(list.map((c) => c.city).filter(Boolean));
    return Array.from(set).sort();
  }, [list]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((c) => {
      const matchQ = !q || c.colonia.toLowerCase().includes(q) || c.city.toLowerCase().includes(q);
      const matchCity = !cityFilter || c.city === cityFilter;
      return matchQ && matchCity;
    });
  }, [list, search, cityFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      city: c.city || "",
      colonia: c.colonia || "",
      factor: String(c.factor ?? "1.00"),
      notes: c.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.city.trim() || !form.colonia.trim()) {
      toast.error("Ciudad y colonia son requeridos");
      return;
    }
    const factorNum = parseFloat(form.factor);
    if (Number.isNaN(factorNum) || factorNum <= 0 || factorNum > 5) {
      toast.error("Factor debe ser un número entre 0.1 y 5.0");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(
          `${API}/admin/valuation/colonias/${editing.id}`,
          { city: form.city, colonia: form.colonia, factor: factorNum, notes: form.notes || null },
          { headers: authHeader() },
        );
        toast.success("Colonia actualizada");
      } else {
        await axios.post(
          `${API}/admin/valuation/colonias`,
          { city: form.city, colonia: form.colonia, factor: factorNum, notes: form.notes || null },
          { headers: authHeader() },
        );
        toast.success("Colonia creada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar la colonia "${c.colonia}"?`)) return;
    try {
      await axios.delete(`${API}/admin/valuation/colonias/${c.id}`, { headers: authHeader() });
      toast.success("Colonia eliminada");
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const factorBadge = (f) => {
    if (f >= 1.30) return { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Premium" };
    if (f >= 1.10) return { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Alta" };
    if (f >= 0.95) return { color: "bg-slate-100 text-slate-700 border-slate-200", label: "Media" };
    return { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Popular" };
  };

  return (
    <div className="space-y-6" data-testid="colonias-manager">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" /> Colonias & Micro-mercados
              </CardTitle>
              <CardDescription>
                Catálogo de colonias con factor de ubicación que multiplica el precio base de ciudad en el módulo MVIP. Estos factores se aplican en la demo pública de valuación y en cálculos del backend.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={load} data-testid="colonias-refresh">
                <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
              </Button>
              <Button size="sm" onClick={openCreate} data-testid="colonias-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Nueva colonia
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar colonia o ciudad..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="colonias-search"
              />
            </div>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="border border-slate-200 rounded-md px-3 text-sm bg-white"
              data-testid="colonias-city-filter"
            >
              <option value="">Todas las ciudades</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex items-center text-sm text-slate-500 px-2">
              <TrendingUp className="w-4 h-4 mr-1.5" /> {filtered.length} / {list.length}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10 text-slate-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No hay colonias con esos filtros.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-600">
                    <th className="py-2 px-3 font-medium">Ciudad</th>
                    <th className="py-2 px-3 font-medium">Colonia</th>
                    <th className="py-2 px-3 font-medium">Factor</th>
                    <th className="py-2 px-3 font-medium">Banda</th>
                    <th className="py-2 px-3 font-medium">Notas</th>
                    <th className="py-2 px-3 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const b = factorBadge(c.factor);
                    return (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`colonia-row-${c.id}`}>
                        <td className="py-2 px-3 text-slate-700">{c.city}</td>
                        <td className="py-2 px-3 font-medium text-slate-900">{c.colonia}</td>
                        <td className="py-2 px-3 font-mono text-slate-800">×{Number(c.factor).toFixed(2)}</td>
                        <td className="py-2 px-3"><Badge variant="outline" className={b.color}>{b.label}</Badge></td>
                        <td className="py-2 px-3 text-slate-500 text-xs">{c.notes || "—"}</td>
                        <td className="py-2 px-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`colonia-edit-${c.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-red-600 hover:text-red-700" data-testid={`colonia-delete-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar colonia" : "Nueva colonia"}</DialogTitle>
            <DialogDescription>
              Define la ciudad, nombre y factor de ubicación. El factor multiplica el precio base de la ciudad para la colonia.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Ciudad</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ciudad de México" data-testid="colonia-form-city" />
            </div>
            <div>
              <Label className="text-xs">Colonia</Label>
              <Input value={form.colonia} onChange={(e) => setForm({ ...form, colonia: e.target.value })} placeholder="Polanco" data-testid="colonia-form-name" />
            </div>
            <div>
              <Label className="text-xs">Factor (0.1 – 5.0)</Label>
              <Input type="number" step="0.01" min="0.1" max="5" value={form.factor} onChange={(e) => setForm({ ...form, factor: e.target.value })} data-testid="colonia-form-factor" />
              <p className="text-[11px] text-slate-500 mt-1">Referencia: 1.00 = precio base. &gt;1.0 zona valorada, &lt;1.0 zona económica.</p>
            </div>
            <div>
              <Label className="text-xs">Notas (opcional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Justificación, fuente, fecha..." data-testid="colonia-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="colonia-form-save">
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear colonia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
